import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  WhmCard,
  WhmCardContent,
  WhmFade,
  WhmGrid,
} from "@securitize/reactjs-whm";
import NameStep from "./NameStep";
import PhoneStep from "./PhoneStep";
import AccountTypeStep from "./AccountTypeStep";
import EntityInfoStep from "./EntityInfoStep";
import { useFormikContext } from "formik";
import { FormType } from "./types";
import {
  REGISTRATION_STEP_PATHS,
  RegistrationSteps,
} from "./routing-constants";
import { useCountries } from "../shared/CountryTextField/CountriesContext";
import { MFEContext } from "../../contexts/MFEContext";
import { MIXPANEL_EVENTS } from "../../helpers/mixpanel";

const StepContainer = forwardRef<
  HTMLDivElement,
  {
    isOpen: boolean;
    children: React.ReactNode;
    onEnter: (here: HTMLDivElement) => void;
  }
>(function StepContainerComponent({ isOpen, children }, ref) {
  return (
    <WhmFade in={isOpen} timeout={isOpen ? 1500 : 500}>
      <WhmCardContent
        ref={ref}
        sx={{
          position: "absolute",
          width: "480px",
          padding: 6,
          "&:last-child": {
            paddingBottom: 6,
          },
        }}
      >
        {children}
      </WhmCardContent>
    </WhmFade>
  );
});

export default function DesktopForm({ step }: { step: RegistrationSteps }) {
  const [height, setHeight] = useState(400);
  const containerRef = useRef(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const accountTypeRef = useRef<HTMLDivElement>(null);
  const entityInfoRef = useRef<HTMLDivElement>(null);
  const formik = useFormikContext<FormType>();
  const countriesQuery = useCountries();
  const { mixPanel } = useContext(MFEContext);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.submitCount > 0) {
      mixPanel.track(MIXPANEL_EVENTS.REGISTRATION.ERROR);
    }
  }, [formik.submitCount, mixPanel]);

  const resizeOnChange = useCallback(() => {
    if (step === REGISTRATION_STEP_PATHS.name) {
      if (nameRef.current) {
        setHeight(nameRef.current.clientHeight);
      }
    }

    if (step === REGISTRATION_STEP_PATHS.phone) {
      if (phoneRef.current) {
        setHeight(phoneRef.current.clientHeight);
      }
    }

    if (step === REGISTRATION_STEP_PATHS.accountType) {
      if (accountTypeRef.current) {
        setHeight(accountTypeRef.current.clientHeight);
      }
    }

    if (step === REGISTRATION_STEP_PATHS.entityInfo) {
      if (entityInfoRef.current) {
        setHeight(entityInfoRef.current.clientHeight);
      }
    }
  }, [step]);

  // Just try to resize whenever Formik changes: e.g., errors.
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated before measuring
    setTimeout(resizeOnChange, 0);
  }, [
    formik,
    step,
    countriesQuery.isLoading,
    countriesQuery.error,
    resizeOnChange,
  ]);

  return (
    <WhmGrid
      container
      sx={{
        width: "100vw",
        height: "100vh",
        position: "absolute",
        top: "0",
        left: "0",
      }}
      direction="row"
      alignContent="center"
      justifyContent="center"
    >
      <WhmGrid item>
        <WhmCard
          ref={containerRef}
          sx={{
            width: "480px",
            height: `${height}px`,
            overflow: "hidden",
            position: "relative",
            transition: "height 0.5s",
          }}
          variant="outlined"
        >
          <StepContainer
            isOpen={step === REGISTRATION_STEP_PATHS.name}
            ref={nameRef}
            onEnter={(here) => {
              setHeight(here.clientHeight);
            }}
          >
            <NameStep />
          </StepContainer>

          <StepContainer
            isOpen={step === REGISTRATION_STEP_PATHS.phone}
            ref={phoneRef}
            onEnter={(here) => {
              setHeight(here.clientHeight);
            }}
          >
            <PhoneStep />
          </StepContainer>

          <StepContainer
            isOpen={step === REGISTRATION_STEP_PATHS.accountType}
            ref={accountTypeRef}
            onEnter={(here) => {
              setHeight(here.clientHeight);
            }}
          >
            <AccountTypeStep />
          </StepContainer>

          <StepContainer
            isOpen={step === REGISTRATION_STEP_PATHS.entityInfo}
            ref={entityInfoRef}
            onEnter={(here) => {
              setHeight(here.clientHeight);
            }}
          >
            <EntityInfoStep />
          </StepContainer>
        </WhmCard>
      </WhmGrid>
    </WhmGrid>
  );
}
